import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Select2Dropdown from '../common/Select2Dropdown';
import { getAuthHeaders } from '../../auth';
import { toast } from 'react-toastify';
import { usePermissions } from '../../services/PermissionsContext';
import '../documentation/JobList.css';
import ConfirmDialog from '../common/ConfirmDialog';
import TableScrollContainer from '../common/TableScrollContainer';
import { openFilePreview } from '../common/FilePreviewModal';

const API_BASE = '/api/documentation/jobs';

const toDisplay = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return String(value).toUpperCase();
};

const RELEASE_ORDER_OPTIONS = [
  { id: 'OBTAINED', text: 'OBTAINED' },
  { id: 'NOT OBTAINED', text: 'NOT OBTAINED' },
  { id: 'N/A', text: 'N/A' },
];

const CopyCell = ({ text, children }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = (e) => {
    e.stopPropagation();
    const val = text != null ? String(text) : (typeof children === 'string' ? children : '');
    if (!val || val === '-') return;
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(val).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      });
    } else {
      const ta = document.createElement('textarea');
      ta.value = val;
      ta.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 1200); } catch {}
      document.body.removeChild(ta);
    }
  };
  return (
    <span className="copy-cell">
      {children}
      <button
        type="button"
        className={`copy-icon-btn${copied ? ' copied' : ''}`}
        title={copied ? 'Copied!' : 'Copy'}
        onClick={handleCopy}
        onMouseDown={e => e.stopPropagation()}
      >
        {copied
          ? <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
          : <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/></svg>
        }
      </button>
    </span>
  );
};

const ExBondReport = () => {
  const { hasPermission } = usePermissions();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dropdowns, setDropdowns] = useState({ remarks: [], dutyOptions: [] });
  const [idfFilter, setIdfFilter] = useState('');
  const [search, setSearch] = useState('');
  const [remarkFilter, setRemarkFilter] = useState('');
  const [drafts, setDrafts] = useState({});
  const [editingCells, setEditingCells] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [activeTab, setActiveTab] = useState('exBondReport');
  const [generatingDaily, setGeneratingDaily] = useState(false);
  const [previewingDaily, setPreviewingDaily] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: () => {} });

  const canEdit = hasPermission('EX BOND REPORTS', 'can_edit');
  const canView = hasPermission('EX BOND REPORTS', 'can_view');
  const effectiveRemarkFilter = activeTab === 'releasedReport' ? 'RELEASED' : remarkFilter;

  const fetchDropdowns = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/ex-bond-dropdowns/`, { headers: getAuthHeaders(false) });
      if (!res.ok) return;
      const data = await res.json();
      setDropdowns({
        remarks: data.remarks || [],
        dutyOptions: data.duty_options || [{ id: 'PAID', text: 'PAID' }, { id: 'NOT PAID', text: 'NOT PAID' }],
      });
    } catch {}
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (effectiveRemarkFilter) params.append('remark_filter', effectiveRemarkFilter);
      const res = await fetch(`${API_BASE}/ex-bond-report/?${params.toString()}`, { headers: getAuthHeaders(false) });
      if (!res.ok) throw new Error('Failed to fetch Ex Bond report.');
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.message || 'Failed to fetch Ex Bond report.');
    } finally {
      setLoading(false);
    }
  }, [effectiveRemarkFilter]);

  const handlePreviewDailyReport = async () => {
    setPreviewingDaily(true);
    try {
      const res = await fetch(`${API_BASE}/preview-daily-ex-bond-report/`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to generate preview.');
      }
      const blob = await res.blob();
      if (!blob.size) throw new Error('The preview PDF is empty.');
      const blobUrl = URL.createObjectURL(blob);
      openFilePreview(blobUrl, `Preview_Ex_Bond_Report_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Failed to generate preview.');
    } finally {
      setPreviewingDaily(false);
    }
  };

  const executeGenerateDailyReport = async () => {
    setGeneratingDaily(true);
    try {
      setIdfFilter('');
      setSearch('');
      const res = await fetch(`${API_BASE}/generate-daily-ex-bond-report/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ report_date: new Date().toISOString().split('T')[0] }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to generate daily ex bond report.');
      }
      toast.success('Daily Ex Bond Report generated and saved successfully.');
    } catch (e) {
      toast.error(e.message || 'Failed to generate daily ex bond report.');
    } finally {
      setGeneratingDaily(false);
    }
  };

  const handleGenerateDailyReport = () => {
    setConfirmDialog({
      isOpen: true,
      title: 'Generate Daily Report',
      message: 'Generating this report will overwrite any previously generated report for today. Are you sure you would like to generate this report now?',
      onConfirm: () => {
        setConfirmDialog({ ...confirmDialog, isOpen: false });
        executeGenerateDailyReport();
      },
      onCancel: () => setConfirmDialog({ ...confirmDialog, isOpen: false }),
    });
  };

  const clearAllFilters = async () => {
    setIdfFilter('');
    setSearch('');
    setRemarkFilter('');
    await fetchRows();
  };

  useEffect(() => {
    fetchDropdowns();
  }, [fetchDropdowns]);

  useEffect(() => {
    if (canView) {
      fetchRows();
    }
  }, [fetchRows, canView]);

  useEffect(() => {
    setDrafts(prev => {
      const next = { ...prev };
      rows.forEach(r => {
        next[r.id] = {
          ...(next[r.id] || {}),
          dutyPaid: !!r.duty_paid,
          releaseOrder: r.release_order || '',
          releaseOrderFile: null,
          remarks: r.airport_remarks || '',
          releaseDate: r.release_date || '',
        };
      });
      return next;
    });
  }, [rows]);

  const updateJobField = async (row, payload) => {
    if (!canEdit) {
      toast.error('You do not have permission to edit Ex Bond Report.');
      return false;
    }
    setSavingId(row.id);
    try {
      const res = await fetch(`${API_BASE}/${row.id}/?module=EX%20BOND%20REPORTS`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Failed to update record.');
      await fetchDropdowns();
      await fetchRows();
      return true;
    } catch (e) {
      toast.error(e.message || 'Failed to update record.');
      return false;
    } finally {
      setSavingId(null);
    }
  };

  const getCellKey = (rowId, field) => `${rowId}:${field}`;

  const isEditingCell = (rowId, field) => !!editingCells[getCellKey(rowId, field)];

  const startEditCell = (row, field) => {
    setDrafts(prev => ({
      ...prev,
      [row.id]: {
        ...(prev[row.id] || {}),
        dutyPaid: !!row.duty_paid,
        releaseOrder: row.release_order || '',
        releaseOrderFile: null,
        remarks: row.airport_remarks || '',
        releaseDate: row.release_date || '',
      },
    }));
    setEditingCells(prev => ({ ...prev, [getCellKey(row.id, field)]: true }));
  };

  const cancelEditCell = (row, field) => {
    setDrafts(prev => ({
      ...prev,
      [row.id]: {
        ...(prev[row.id] || {}),
        dutyPaid: !!row.duty_paid,
        releaseOrder: row.release_order || '',
        releaseOrderFile: null,
        remarks: row.airport_remarks || '',
        releaseDate: row.release_date || '',
      },
    }));
    setEditingCells(prev => {
      const next = { ...prev };
      delete next[getCellKey(row.id, field)];
      return next;
    });
  };

  const saveEditCell = async (row, field, payload) => {
    const saved = await updateJobField(row, payload);
    if (saved) {
      setEditingCells(prev => {
        const next = { ...prev };
        delete next[getCellKey(row.id, field)];
        return next;
      });
    }
  };

  const handleSaveReleaseOrder = async (row) => {
    if (!canEdit) {
      toast.error('You do not have permission to edit the Ex Bond report.');
      return;
    }
    const draft = drafts[row.id] || {};
    const value = (draft.releaseOrder || '').toUpperCase();
    const existingDoc = row.release_order_document;
    if (value === 'OBTAINED' && !existingDoc && !draft.releaseOrderFile) {
      toast.error('Please upload a Release Order document.');
      return;
    }
    setSavingId(row.id);
    try {
      let uploadResult = existingDoc || null;
      if (value === 'OBTAINED' && draft.releaseOrderFile) {
        const formData = new FormData();
        formData.append('document_type', 'Release Order');
        formData.append('document_file', draft.releaseOrderFile);
        const res = await fetch(`${API_BASE}/${row.id}/upload-job-document/?module=EX%20BOND%20REPORTS`, {
          method: 'POST',
          headers: getAuthHeaders(true),
          body: formData,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Failed to upload Release Order document.');
        uploadResult = data;
      }
      const patchRes = await fetch(`${API_BASE}/${row.id}/?module=EX%20BOND%20REPORTS`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ border_release_order: draft.releaseOrder || null }),
      });
      if (!patchRes.ok) throw new Error('Failed to update release order.');
      await fetchRows();
      await fetchDropdowns();
      setEditingCells(prev => {
        const next = { ...prev };
        delete next[getCellKey(row.id, 'releaseOrder')];
        return next;
      });
    } catch (e) {
      toast.error(e.message || 'Failed to save release order.');
    } finally {
      setSavingId(null);
    }
  };

  const getPendingSectionRemark = (r) => {
    const jobStatus = Array.isArray(r.job_status) ? r.job_status : [];
    if (jobStatus.some(s => String(s).toUpperCase() === 'COMPLETE')) {
      return r.assessment_status_label || r.assessment_status || '';
    }
    return r.status_remark || '';
  };

  const remarkOptions = useMemo(() => {
    const values = (dropdowns.remarks || []).map(r => (typeof r === 'string' ? r : r.text)).filter(Boolean);
    if (!values.includes('RELEASED')) values.push('RELEASED');
    return Array.from(new Set(values)).map(v => ({ id: v, text: v }));
  }, [dropdowns.remarks]);

  const getRemarkOptionsForRow = (row) => {
    const isDutyPaid = (row.duty_status || '').toUpperCase() === 'PAID';
    const isReleaseOrderObtained = (row.release_order || '').toUpperCase() === 'OBTAINED';
    return remarkOptions.filter(opt => opt.id !== 'RELEASED' || (isDutyPaid && isReleaseOrderObtained));
  };

  const filteredRows = useMemo(() => {
    let data = [...rows];
    if (idfFilter.trim()) {
      const needle = idfFilter.trim().toUpperCase();
      data = data.filter(r => (r.idf_no || '').toUpperCase().includes(needle));
    }
    if (search.trim()) {
      const needle = search.trim().toUpperCase();
      data = data.filter(r =>
        [
          r.idf_no,
          r.tansad_no,
          r.repack_bl,
          r.importer,
          r.airport_remarks,
        ].some(field => field && field.toString().toUpperCase().includes(needle))
      );
    }
    return data;
  }, [rows, idfFilter, search]);

  const acceptedRows = filteredRows.filter(r => {
    const status = (r.assessment_status || '').toUpperCase();
    return status === 'ACCEPTED' || status === 'SENT';
  });
  const pendingRows = filteredRows.filter(r => {
    const status = (r.assessment_status || '').toUpperCase();
    return status !== 'ACCEPTED' && status !== 'SENT';
  });

  const renderTable = (dataRows, { isAccepted = false, pendingSection = false } = {}) => {
    const colSpan = isAccepted ? 7 : 5;
    return (
      <TableScrollContainer>
        <table className="jobs-table" style={{ overflow: 'visible' }}>
          <thead>
            <tr>
              <th>IDF NO</th>
              <th>TANSAD</th>
              <th>REPACK BL</th>
              <th>IMPORTER</th>
              {isAccepted && <th>DUTY</th>}
              {isAccepted && <th>RELEASE ORDER</th>}
              <th>REMARKS</th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={colSpan} style={{ textAlign: 'center' }}>Loading...</td></tr>)}
            {!loading && dataRows.length === 0 && (<tr><td colSpan={colSpan} className="no-data">No records found.</td></tr>)}
            {!loading && dataRows.map(r => (
              <tr key={r.id}>
                <td><CopyCell text={r.idf_no}>{toDisplay(r.idf_no)}</CopyCell></td>
                <td><CopyCell text={r.tansad_no}>{toDisplay(r.tansad_no)}</CopyCell></td>
                <td><CopyCell text={r.repack_bl}>{toDisplay(r.repack_bl)}</CopyCell></td>
                <td><CopyCell text={r.importer}><span>{toDisplay(r.importer)}</span></CopyCell>{effectiveRemarkFilter !== 'RELEASED' && r.is_fund_request && !r.fund_request_released && (r.airport_remarks || '').toUpperCase() !== 'RELEASED' && <i className="fa fa-exclamation-triangle" style={{ color: '#e74c3c', marginLeft: 6, animation: 'port-danger-flash 1s infinite' }} title="Fund Request" />}</td>
                {isAccepted && (
                  <td style={{ textAlign: 'center' }}>
                    {canEdit && isEditingCell(r.id, 'duty') ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                        <input
                          type="checkbox"
                          checked={!!drafts[r.id]?.dutyPaid}
                          onChange={e => setDrafts(prev => ({
                            ...prev,
                            [r.id]: { ...(prev[r.id] || {}), dutyPaid: e.target.checked },
                          }))}
                          disabled={savingId === r.id}
                          style={{ margin: 0 }}
                        />
                        <button className="icon-btn save-btn" title="Save" disabled={savingId === r.id} onClick={() => saveEditCell(r, 'duty', { duty_paid: !!drafts[r.id]?.dutyPaid })}><i className="fa fa-save" /></button>
                        <button className="icon-btn cancel-btn" title="Cancel" onClick={() => cancelEditCell(r, 'duty')}><i className="fa fa-times" /></button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                        <span style={{ fontSize: '13px', fontWeight: '500' }}>{toDisplay(r.duty_status)}</span>
                        {canEdit && <button type="button" className="icon-btn" title="Edit Duty" onClick={() => startEditCell(r, 'duty')}><i className="fa fa-pencil" /></button>}
                      </div>
                    )}
                  </td>
                )}
                {isAccepted && (
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {canEdit ? isEditingCell(r.id, 'releaseOrder') ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, flexWrap: 'wrap' }}>
                        <Select2Dropdown
                          options={RELEASE_ORDER_OPTIONS}
                          value={drafts[r.id]?.releaseOrder || ''}
                          onChange={val => setDrafts(prev => ({
                            ...prev,
                            [r.id]: { ...(prev[r.id] || {}), releaseOrder: val || '', releaseOrderFile: null },
                          }))}
                          valueKey="id"
                          displayKey="text"
                          placeholder="Select"
                          clearable
                          style={{ minWidth: 150 }}
                          disabled={savingId === r.id}
                        />
                        {drafts[r.id]?.releaseOrder === 'OBTAINED' && (
                          <>
                            <input
                              type="file"
                              id={`ex-release-order-file-${r.id}`}
                              accept=".pdf,.png,.jpg,.jpeg"
                              style={{ display: 'none' }}
                              onChange={e => {
                                const file = e.target.files?.[0] || null;
                                setDrafts(prev => ({ ...prev, [r.id]: { ...(prev[r.id] || {}), releaseOrderFile: file } }));
                              }}
                            />
                            <label htmlFor={`ex-release-order-file-${r.id}`} className="icon-btn" title="Upload Release Order" style={{ cursor: 'pointer' }}>
                              <i className="fa fa-upload" />
                            </label>
                            {drafts[r.id]?.releaseOrderFile && (
                              <span style={{ fontSize: 11, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={drafts[r.id].releaseOrderFile.name}>{drafts[r.id].releaseOrderFile.name}</span>
                            )}
                          </>
                        )}
                        {r.release_order_document && (
                          <button type="button" className="icon-btn" title="View Release Order" onClick={() => openFilePreview(r.release_order_document.document_file_url, 'Release Order')}><i className="fa fa-eye" /></button>
                        )}
                        <button className="icon-btn save-btn" title="Save" disabled={savingId === r.id} onClick={() => handleSaveReleaseOrder(r)}><i className="fa fa-save" /></button>
                        <button className="icon-btn cancel-btn" title="Cancel" onClick={() => cancelEditCell(r, 'releaseOrder')}><i className="fa fa-times" /></button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                        <CopyCell text={r.release_order}><span>{toDisplay(r.release_order)}</span></CopyCell>
                        {r.release_order_document && (
                          <button type="button" className="icon-btn" title="View Release Order" onClick={() => openFilePreview(r.release_order_document.document_file_url, 'Release Order')}><i className="fa fa-eye" /></button>
                        )}
                        <button type="button" className="icon-btn" title="Edit Release Order" onClick={() => startEditCell(r, 'releaseOrder')}><i className="fa fa-pencil" /></button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                        <CopyCell text={r.release_order}>{toDisplay(r.release_order)}</CopyCell>
                        {r.release_order_document && (
                          <button type="button" className="icon-btn" title="View Release Order" onClick={() => openFilePreview(r.release_order_document.document_file_url, 'Release Order')}><i className="fa fa-eye" /></button>
                        )}
                      </div>
                    )}
                  </td>
                )}
                <td style={{ minWidth: 220 }}>
                  {pendingSection ? (
                    <CopyCell text={getPendingSectionRemark(r)}><span style={{ whiteSpace: 'pre-wrap' }}>{toDisplay(getPendingSectionRemark(r))}</span></CopyCell>
                  ) : canEdit && isEditingCell(r.id, 'remarks') ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Select2Dropdown
                            options={getRemarkOptionsForRow(r)}
                            value={drafts[r.id]?.remarks || ''}
                            onChange={val => setDrafts(prev => ({
                              ...prev,
                              [r.id]: { ...(prev[r.id] || {}), remarks: val || '', releaseDate: val !== 'RELEASED' ? '' : (prev[r.id]?.releaseDate || '') },
                            }))}
                            valueKey="id"
                            displayKey="text"
                            placeholder="SELECT REMARK"
                            clearable
                            style={{ width: '100%' }}
                            disabled={savingId === r.id}
                          />
                        </div>
                        <button className="icon-btn save-btn" title="Save" disabled={savingId === r.id} onClick={() => {
                          const remark = (drafts[r.id]?.remarks || '').trim();
                          if (remark === 'RELEASED' && !(drafts[r.id]?.releaseDate)) {
                            toast.error('Please enter a Release Date before saving RELEASED.');
                            return;
                          }
                          if (remark === 'RELEASED' && r.is_fund_request && !r.fund_request_released) {
                            toast.error('Cannot Release. Awaiting Funds from Client');
                            return;
                          }
                          const payload = { airport_remarks: remark || null };
                          if (remark === 'RELEASED' && drafts[r.id]?.releaseDate) payload.release_date = drafts[r.id].releaseDate;
                          saveEditCell(r, 'remarks', payload);
                        }}><i className="fa fa-save" /></button>
                        <button className="icon-btn cancel-btn" title="Cancel" onClick={() => cancelEditCell(r, 'remarks')}><i className="fa fa-times" /></button>
                      </div>
                      {(drafts[r.id]?.remarks || '') === 'RELEASED' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <label style={{ fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', marginBottom: 0 }}>Release Date <span style={{ color: 'red' }}>*</span></label>
                          <input
                            type="date"
                            className="form-control"
                            style={{ fontSize: 12, padding: '2px 6px', height: 'auto', width: 130 }}
                            value={drafts[r.id]?.releaseDate || ''}
                            onChange={e => setDrafts(prev => ({ ...prev, [r.id]: { ...(prev[r.id] || {}), releaseDate: e.target.value } }))}
                            required
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <CopyCell text={r.airport_remarks}><span style={{ whiteSpace: 'pre-wrap' }}>{effectiveRemarkFilter !== 'RELEASED' && r.is_fund_request && !r.fund_request_released && (r.airport_remarks || '').toUpperCase() !== 'RELEASED' && <i className="fa fa-exclamation-triangle" style={{ color: '#e74c3c', marginRight: 6, animation: 'port-danger-flash 1s infinite' }} title="Fund Request" />}{toDisplay(r.airport_remarks)}</span></CopyCell>
                      {canEdit && <button type="button" className="icon-btn" title="Edit Remarks" onClick={() => startEditCell(r, 'remarks')}><i className="fa fa-pencil" /></button>}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScrollContainer>
    );
  };

  if (!canView) {
    return (
      <div className="permission-denied-container" style={{ padding: '20px' }}>
        <h2>Permission Denied</h2>
        <p>You do not have permission to view Ex Bond Report.</p>
      </div>
    );
  }

  return (
    <div className="job-list-container" style={{ overflowX: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>Ex Bond Reports</h2>
        <button onClick={fetchRows} title="Refresh" style={{ background: 'none', border: '1px solid transparent', borderRadius: 6, cursor: 'pointer', fontSize: 18, color: '#444', padding: '4px 8px', transition: 'background 0.15s, border-color 0.15s' }} onMouseEnter={e => { e.currentTarget.style.background = '#f0f0f0'; e.currentTarget.style.borderColor = '#bbb'; }} onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.borderColor = 'transparent'; }}><i className="fa-solid fa-rotate-right" style={{ fontWeight: 900 }} /></button>
      </div>

      <div className="custom-tabs-container" style={{ marginBottom: 16 }}>
        <button className={`custom-tab ${activeTab === 'exBondReport' ? 'active' : ''}`} onClick={() => setActiveTab('exBondReport')}>
          Ex Bond Report
        </button>
        <button className={`custom-tab ${activeTab === 'dailyExBondReports' ? 'active' : ''}`} onClick={() => setActiveTab('dailyExBondReports')}>
          Daily Ex Bond Report
        </button>
        <button className={`custom-tab ${activeTab === 'releasedReport' ? 'active' : ''}`} onClick={() => setActiveTab('releasedReport')}>
          Released Report
        </button>
      </div>

      <div className="toolbar" style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ minWidth: 180 }}>
          <input
            type="text"
            placeholder="IDF NO"
            className="search-input"
            value={idfFilter}
            onChange={e => setIdfFilter(e.target.value)}
            style={{ textTransform: 'uppercase' }}
          />
        </div>
        <div style={{ minWidth: 180 }}>
          <Select2Dropdown
            options={[{ id: '', text: 'ALL REMARKS' }, ...remarkOptions]}
            value={effectiveRemarkFilter}
            onChange={setRemarkFilter}
            placeholder="FILTER BY REMARK"
            displayKey="text"
            valueKey="id"
            clearable
            isDisabled={activeTab === 'releasedReport'}
          />
        </div>
        <div style={{ minWidth: 220, flex: 1 }}>
          <input
            type="text"
            placeholder="SEARCH"
            className="search-input"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ textTransform: 'uppercase' }}
          />
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button className="add-btn" style={{ background: '#6c757d', whiteSpace: 'nowrap' }} onClick={clearAllFilters}>Clear All Filters</button>
          {activeTab === 'exBondReport' && (
            <>
              <button className="add-btn" style={{ background: '#e67e22' }} onClick={handlePreviewDailyReport} disabled={previewingDaily || generatingDaily}>
                {previewingDaily ? 'Loading Preview...' : 'Preview Daily Report'}
              </button>
              <button className="add-btn" style={{ background: '#3f6b32' }} onClick={handleGenerateDailyReport} disabled={generatingDaily || previewingDaily}>
                {generatingDaily ? 'Generating...' : 'Generate Daily Report'}
              </button>
            </>
          )}
        </div>
      </div>

      {activeTab === 'exBondReport' && (
        <>
          <div style={{ marginBottom: 26 }}>
            <div className="report-section-header" style={{ fontWeight: 'bold', marginBottom: 8, background: '#d9ecff', borderRadius: 6, padding: 8, fontSize: 16 }}>
              EX BOND REPORT - ASSESSMENT ACCEPTED / SENT
            </div>
            {renderTable(acceptedRows, { isAccepted: true })}
          </div>

          <div>
            <div className="report-section-header" style={{ fontWeight: 'bold', marginBottom: 8, background: '#ffe3bf', borderRadius: 6, padding: 8, fontSize: 16 }}>
              PENDING ASSESSMENTS
            </div>
            {renderTable(pendingRows, { isAccepted: false, pendingSection: true })}
          </div>
        </>
      )}

      {activeTab === 'dailyExBondReports' && (
        <DailyExBondReportsTab hasPermission={hasPermission} />
      )}

      {activeTab === 'releasedReport' && (
        <div>
          <div className="report-section-header" style={{ fontWeight: 'bold', marginBottom: 8, background: '#c2fcc2', borderRadius: 6, padding: 8, fontSize: 16 }}>
            RELEASED REPORT
          </div>
          {renderTable(filteredRows, { isAccepted: true })}
        </div>
      )}

      <ConfirmDialog
        open={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={confirmDialog.onCancel}
      />
    </div>
  );
};

function formatDailyDate(dateStr, createdAt) {
  const pad = n => n.toString().padStart(2, '0');
  let datePart = dateStr || '';
  if (datePart) {
    const d = new Date(datePart);
    if (!isNaN(d)) datePart = `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  }
  let timePart = '';
  if (createdAt) {
    const t = new Date(createdAt);
    if (!isNaN(t)) timePart = `${pad(t.getHours())}:${pad(t.getMinutes())}:${pad(t.getSeconds())}`;
  }
  return { datePart, timePart };
}

const DailyExBondReportsTab = ({ hasPermission }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [singleDate, setSingleDate] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const handleViewPdf = useCallback(async (row) => {
    try {
      const res = await fetch(row.view_pdf_url, { headers: getAuthHeaders(false) });
      if (!res.ok) throw new Error('Failed to load PDF preview.');

      const blob = await res.blob();
      if (!blob || blob.size === 0) throw new Error('The generated PDF file is empty.');

      const blobUrl = URL.createObjectURL(blob);
      const previewWindow = openFilePreview(blobUrl, `Daily_Ex_Bond_Report_${row.date}.pdf`);
      if (previewWindow && previewWindow.addEventListener) {
        previewWindow.addEventListener('beforeunload', () => URL.revokeObjectURL(blobUrl), { once: true });
      } else {
        setTimeout(() => URL.revokeObjectURL(blobUrl), 5 * 60 * 1000);
      }
    } catch (e) {
      toast.error(e.message || 'Could not open PDF preview.');
    }
  }, []);

  const fetchDailyReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (singleDate) {
        params.append('date', singleDate);
      } else {
        if (fromDate) params.append('from_date', fromDate);
        if (toDate) params.append('to_date', toDate);
      }
      const res = await fetch(`${API_BASE}/daily-ex-bond-reports/?${params.toString()}`, { headers: getAuthHeaders(false) });
      if (!res.ok) throw new Error('Failed to fetch daily ex bond reports.');
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.message || 'Failed to fetch daily ex bond reports.');
    } finally {
      setLoading(false);
    }
  }, [singleDate, fromDate, toDate]);

  useEffect(() => {
    fetchDailyReports();
  }, [fetchDailyReports]);

  if (!hasPermission('EX BOND REPORTS', 'can_view')) {
    return <div style={{ padding: 20 }}><h2>Permission Denied</h2><p>You do not have permission to view Daily Ex Bond Reports.</p></div>;
  }

  return (
    <div>
      <div className="toolbar" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14, alignItems: 'flex-end' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, color: '#444', fontWeight: 600 }}>Please Enter Single Date</label>
          <input type="date" className="search-input" value={singleDate} onChange={e => setSingleDate(e.target.value)} title="Single Date" />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, color: '#444', fontWeight: 600 }}>Please Enter Date Range</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="date" className="search-input" value={fromDate} onChange={e => setFromDate(e.target.value)} title="From Date" />
            <span style={{ color: '#666', fontWeight: 700 }}>To</span>
            <input type="date" className="search-input" value={toDate} onChange={e => setToDate(e.target.value)} title="To Date" />
          </div>
        </div>
        <button className="add-btn" onClick={fetchDailyReports}>Apply Date Filter</button>
        <button className="add-btn" style={{ background: '#6c757d' }} onClick={() => { setSingleDate(''); setFromDate(''); setToDate(''); }}>Clear Date Filter</button>
      </div>

      <TableScrollContainer>
        <table className="jobs-table" style={{ overflow: 'visible' }}>
          <thead>
            <tr>
              <th>DATE</th>
              <th>GENERATED BY</th>
              <th>VIEW PDF</th>
              <th>DOWNLOAD PDF</th>
              <th>DOWNLOAD EXCEL</th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={5} style={{ textAlign: 'center' }}>Loading...</td></tr>)}
            {!loading && rows.length === 0 && (<tr><td colSpan={5} className="no-data">No daily reports found.</td></tr>)}
            {!loading && rows.map(r => (
              <tr key={r.id}>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {(() => { const { datePart, timePart } = formatDailyDate(r.date, r.created_at); return (<span>{datePart}{timePart && <><br /><span style={{ fontSize: 11, color: '#555' }}>{timePart}</span></>}</span>); })()}
                </td>
                <td>{toDisplay(r.generated_by)}</td>
                <td>
                  <button type="button" className="icon-btn" title="View PDF" onClick={() => handleViewPdf(r)}>
                    <i className="fa fa-eye" />
                  </button>
                </td>
                <td>
                  <a href={r.download_pdf_url} className="icon-btn" title="Download PDF">
                    <i className="fa fa-download" />
                  </a>
                </td>
                <td>
                  <a href={r.download_excel_url} className="icon-btn" title="Download Excel">
                    <i className="fa fa-download" />
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScrollContainer>
    </div>
  );
};

export default ExBondReport;
